import type { AvailableApp } from "@tc-superpods/app-contract";

type RemoteStyleEntry = Pick<AvailableApp, "entry" | "id">;

interface RemoteAssetList {
  async?: string[];
  sync?: string[];
}

interface RemoteExpose {
  assets?: {
    css?: RemoteAssetList;
  };
  name?: string;
  path?: string;
}

interface RemoteManifest {
  exposes?: RemoteExpose[];
}

export interface RemoteStyleBoundary {
  attach: () => Promise<void>;
  cleanup: () => void;
}

const appExposePath = "./App";
const managedStyleAttribute = "data-platform-remote-style-managed";
const remoteStyleAttribute = "data-platform-remote-style";
const remoteStyleHrefAttribute = "data-platform-remote-style-href";
const remoteStyleViteIdAttribute = "data-platform-remote-style-vite-id";
const viteStyleIdAttribute = "data-vite-dev-id";
const cachedViteStylesByRemote = new Map<string, Map<string, HTMLStyleElement>>();
const managedStyleContentObservers = new WeakMap<HTMLStyleElement, MutationObserver>();
const noOpBoundary: RemoteStyleBoundary = {
  attach: async () => {},
  cleanup: () => {}
};

export async function createRemoteStyleBoundary(
  entry: RemoteStyleEntry,
  shadowRoot: ShadowRoot
): Promise<RemoteStyleBoundary> {
  if (!document.head) {
    return noOpBoundary;
  }

  const cssUrls = await getRemoteCssAssetUrls(entry);
  const cssUrlSet = new Set(cssUrls);
  const cachedViteStyles = getOrCreateCachedViteStyles(entry);
  const observer = new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        const stylesheetLink = asStylesheetLink(node);
        const viteStyle = asViteStyle(node);

        if (stylesheetLink) {
          maybeMoveRuntimeLink(stylesheetLink);
          continue;
        }

        if (!viteStyle) {
          continue;
        }

        maybeMoveViteStyle(viteStyle);
      }
    }
  });

  observer.observe(document.head, { childList: true });
  moveExistingHeadStyles();

  return {
    attach: async () => {
      moveExistingHeadStyles();

      await Promise.all(
        cssUrls.map(async (cssUrl) => {
          const link = ensureShadowStylesheet(cssUrl);
          await waitForStylesheet(link);
        })
      );

      ensureShadowViteStyles();
    },
    cleanup: () => {
      observer.disconnect();
    }
  };

  function moveExistingHeadStyles(): void {
    for (const link of document.head.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]')) {
      maybeMoveRuntimeLink(link);
    }

    for (const style of document.head.querySelectorAll<HTMLStyleElement>(`style[${viteStyleIdAttribute}]`)) {
      maybeMoveViteStyle(style);
    }
  }

  function maybeMoveRuntimeLink(link: HTMLLinkElement): void {
    const resolvedHref = normalizeHref(link.href);

    if (!cssUrlSet.has(resolvedHref)) {
      return;
    }

    moveLinkToShadow(link, resolvedHref);
  }

  function maybeMoveViteStyle(style: HTMLStyleElement): void {
    const viteStyleId = getViteStyleId(style);

    if (!viteStyleId) {
      return;
    }

    syncManagedStyleContent(style);
    cachedViteStyles.set(viteStyleId, style);
    moveStyleToShadow(style, viteStyleId);
  }

  function ensureShadowViteStyles(): void {
    for (const [viteStyleId, style] of cachedViteStyles) {
      syncManagedStyleContent(style);
      moveStyleToShadow(style, viteStyleId);
    }
  }

  function ensureShadowStylesheet(cssUrl: string): HTMLLinkElement {
    const existingShadowLink = findShadowLink(cssUrl);

    if (existingShadowLink) {
      return existingShadowLink;
    }

    const existingHeadLink = findHeadLink(cssUrl);

    if (existingHeadLink) {
      return moveLinkToShadow(existingHeadLink, cssUrl);
    }

    const managedLink = document.createElement("link");
    managedLink.rel = "stylesheet";
    managedLink.href = cssUrl;
    managedLink.setAttribute(managedStyleAttribute, "true");
    markLink(managedLink, cssUrl);
    shadowRoot.append(managedLink);

    return managedLink;
  }

  function moveLinkToShadow(link: HTMLLinkElement, cssUrl: string): HTMLLinkElement {
    const existingShadowLink = findShadowLink(cssUrl);

    if (existingShadowLink && existingShadowLink !== link) {
      if (link.parentNode !== shadowRoot) {
        shadowRoot.append(link);
      }

      markLink(link, cssUrl);

      if (existingShadowLink.getAttribute(managedStyleAttribute) === "true") {
        if (isStylesheetLoaded(link)) {
          existingShadowLink.remove();
        } else {
          link.addEventListener(
            "load",
            () => {
              existingShadowLink.remove();
            },
            { once: true }
          );
        }

        return link;
      }

      if (link.parentNode === shadowRoot) {
        link.remove();
      }

      return existingShadowLink;
    }

    if (link.parentNode !== shadowRoot) {
      shadowRoot.append(link);
    }

    markLink(link, cssUrl);

    return link;
  }

  function moveStyleToShadow(style: HTMLStyleElement, viteStyleId: string): HTMLStyleElement {
    const existingShadowStyle = findShadowStyle(viteStyleId);

    if (existingShadowStyle && existingShadowStyle !== style) {
      existingShadowStyle.remove();
    }

    if (style.parentNode !== shadowRoot) {
      shadowRoot.append(style);
    }

    markStyle(style, viteStyleId);

    return style;
  }

  function findHeadLink(cssUrl: string): HTMLLinkElement | null {
    for (const link of document.head.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]')) {
      if (normalizeHref(link.href) === cssUrl) {
        return link;
      }
    }

    return null;
  }

  function findShadowLink(cssUrl: string): HTMLLinkElement | null {
    for (const link of shadowRoot.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]')) {
      if (normalizeHref(link.href) === cssUrl) {
        return link;
      }
    }

    return null;
  }

  function findShadowStyle(viteStyleId: string): HTMLStyleElement | null {
    for (const style of shadowRoot.querySelectorAll<HTMLStyleElement>(`style[${viteStyleIdAttribute}]`)) {
      if (style.getAttribute(viteStyleIdAttribute) === viteStyleId) {
        return style;
      }
    }

    return null;
  }
}

async function getRemoteCssAssetUrls(entry: RemoteStyleEntry): Promise<string[]> {
  try {
    const remoteEntryUrl = new URL(entry.entry, window.location.href);
    const manifestUrl = new URL("mf-manifest.json", remoteEntryUrl);
    const response = await fetch(manifestUrl, {
      cache: "no-store"
    });

    if (!response.ok) {
      return [];
    }

    const manifest = await response.json();

    if (!isRemoteManifest(manifest)) {
      return [];
    }

    const appExpose = manifest.exposes?.find((expose) => expose.path === appExposePath || expose.name === "App");
    const cssAssets = [
      ...(appExpose?.assets?.css?.sync ?? []),
      ...(appExpose?.assets?.css?.async ?? [])
    ];

    return [...new Set(cssAssets.map((assetPath) => new URL(assetPath, remoteEntryUrl).href))];
  } catch {
    return [];
  }
}

function markLink(link: HTMLLinkElement, cssUrl: string): void {
  link.setAttribute(remoteStyleAttribute, "");
  link.setAttribute(remoteStyleHrefAttribute, cssUrl);
}

function markStyle(style: HTMLStyleElement, viteStyleId: string): void {
  style.setAttribute(remoteStyleAttribute, "");
  style.setAttribute(remoteStyleViteIdAttribute, viteStyleId);
}

function waitForStylesheet(link: HTMLLinkElement): Promise<void> {
  if (isStylesheetLoaded(link)) {
    return Promise.resolve();
  }

  return new Promise<void>((resolve, reject) => {
    const handleLoad = (): void => {
      cleanup();
      resolve();
    };
    const handleError = (): void => {
      cleanup();
      reject(new Error(`Remote stylesheet failed to load: ${link.href}`));
    };
    const cleanup = (): void => {
      link.removeEventListener("load", handleLoad);
      link.removeEventListener("error", handleError);
    };

    link.addEventListener("load", handleLoad);
    link.addEventListener("error", handleError);
  });
}

function isStylesheetLoaded(link: HTMLLinkElement): boolean {
  return link.sheet !== null;
}

function asStylesheetLink(node: Node): HTMLLinkElement | null {
  if (!(node instanceof HTMLLinkElement)) {
    return null;
  }

  return node.rel === "stylesheet" ? node : null;
}

function asViteStyle(node: Node): HTMLStyleElement | null {
  if (!(node instanceof HTMLStyleElement)) {
    return null;
  }

  return getViteStyleId(node) ? node : null;
}

function getViteStyleId(style: HTMLStyleElement): string | null {
  const viteStyleId = style.getAttribute(viteStyleIdAttribute);

  return viteStyleId ? viteStyleId : null;
}

function getOrCreateCachedViteStyles(entry: RemoteStyleEntry): Map<string, HTMLStyleElement> {
  const cacheKey = `${entry.id}::${entry.entry}`;
  const cachedStyles = cachedViteStylesByRemote.get(cacheKey);

  if (cachedStyles) {
    return cachedStyles;
  }

  const nextCachedStyles = new Map<string, HTMLStyleElement>();
  cachedViteStylesByRemote.set(cacheKey, nextCachedStyles);

  return nextCachedStyles;
}

function syncManagedStyleContent(style: HTMLStyleElement): void {
  observeManagedStyleContent(style);

  const cssText = style.textContent ?? "";
  const rewrittenCssText = rewriteCssForShadowBoundary(cssText);

  if (rewrittenCssText !== cssText) {
    style.textContent = rewrittenCssText;
  }
}

function observeManagedStyleContent(style: HTMLStyleElement): void {
  if (managedStyleContentObservers.has(style)) {
    return;
  }

  const observer = new MutationObserver(() => {
    syncManagedStyleContent(style);
  });

  observer.observe(style, {
    childList: true,
    characterData: true,
    subtree: true
  });
  managedStyleContentObservers.set(style, observer);
}

function rewriteCssForShadowBoundary(cssText: string): string {
  if (!hasGlobalCssSelectors(cssText)) {
    return cssText;
  }

  try {
    const stylesheet = new CSSStyleSheet();
    stylesheet.replaceSync(cssText);
    rewriteCssRulesForShadowBoundary(stylesheet.cssRules);

    return Array.from(stylesheet.cssRules)
      .map((rule) => rule.cssText)
      .join("\n");
  } catch {
    return rewriteSelectorTextForShadowBoundary(cssText);
  }
}

function rewriteCssRulesForShadowBoundary(rules: CSSRuleList): void {
  for (const rule of Array.from(rules)) {
    if (rule instanceof CSSStyleRule) {
      const rewrittenSelector = rewriteSelectorTextForShadowBoundary(rule.selectorText);

      if (rewrittenSelector !== rule.selectorText) {
        rule.selectorText = rewrittenSelector;
      }

      continue;
    }

    if (hasNestedCssRules(rule)) {
      rewriteCssRulesForShadowBoundary(rule.cssRules);
    }
  }
}

function rewriteSelectorTextForShadowBoundary(selectorText: string): string {
  return selectorText
    .replace(/(^|[^\w-]):root(?![\w-])/g, "$1:host")
    .replace(/(^|[^\w-])html(?![\w-])/g, "$1:host")
    .replace(/(^|[^\w-])body(?![\w-])/g, "$1:host")
    .replace(/:host\s+:host/g, ":host");
}

function hasGlobalCssSelectors(cssText: string): boolean {
  return cssText.includes(":root") || cssText.includes("html") || cssText.includes("body");
}

function hasNestedCssRules(rule: CSSRule): rule is CSSRule & { cssRules: CSSRuleList } {
  return "cssRules" in rule;
}

function normalizeHref(value: string): string {
  return new URL(value, window.location.href).href;
}

function isRemoteManifest(value: unknown): value is RemoteManifest {
  if (!isRecord(value)) {
    return false;
  }

  return (
    value.exposes === undefined ||
    (Array.isArray(value.exposes) && value.exposes.every(isRemoteExpose))
  );
}

function isRemoteExpose(value: unknown): value is RemoteExpose {
  if (!isRecord(value)) {
    return false;
  }

  return (
    (value.path === undefined || typeof value.path === "string") &&
    (value.name === undefined || typeof value.name === "string") &&
    (value.assets === undefined || isRecord(value.assets))
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
